// @flow
import React from 'react';
import {FormattedMessage} from 'react-intl';
import Experiment from '@homeaway/react-experiment';
import {SVGIcon} from '@homeaway/react-svg';
import IconInstantBook from '../IconButton/IconInstantBook';
import {filter as filterGraphQlAnywhere} from 'graphql-anywhere';
import Price, {fragments as priceFragments} from '../Price/Price';
import Rating, {fragments as ratingFragments} from '../Rating/Rating';
import Details from '../Details/Details';
import {
    HA_SERP_FREE_CANCELLATION_V2,
    HA_SERP_MOBILE_GEO_DISTANCE
} from '../../abacus/experiments';
import HitFreeCancellation from '../HitFreeCancellation/HitFreeCancellation';
import GeoDistance from '../GeoDistance/GeoDistance';
import HitLabel from '../HitLabel/HitLabel';
import {InstantBookingWrapper} from './HitInfo';

import type {Props as HitInfoProps} from './HitInfo';
import type {Badge} from './HitInfo';

type Props = {
    exclusivityBadge: ?Badge,
    instantBookableTitle: string,
    premierPartnerBadge: ?Badge
} & HitInfoProps;

// TODO: move minStay logic to lingua
// https://jira.homeawaycorp.com/browse/SERP-2848
export default function HitInfoMobile({
    brand,
    exclusivityBadge,
    instantBookableTitle,
    intl,
    isDatelessSearch,
    isMetric,
    listing,
    premierPartnerBadge,
    hit,
    fullLocale
}: Props) {
    const {freeCancellationUntil, geoDistance} = hit;
    const {instantBookable, minStayRange, reviewBadges, reviewCount} = listing;
    return (
        <div className="HitInfo HitInfo--mobile">
            <div className="HitInfo__content">
                <div className="HitInfo__priceSuperlativeGroup">
                    <div className="HitInfo__price">
                        {instantBookable && (
                            <InstantBookingWrapper title={instantBookableTitle}>
                                <SVGIcon markup={IconInstantBook} />
                            </InstantBookingWrapper>
                        )}
                        <Price
                            hit={filterGraphQlAnywhere(
                                priceFragments.SERPHit,
                                hit
                            )}
                            listing={filterGraphQlAnywhere(
                                priceFragments.listing,
                                listing
                            )}
                            useAverage={isDatelessSearch}
                            fullLocale={fullLocale}
                        />
                    </div>
                    {!!reviewBadges && (
                        <span className="HitInfo__superlative small">
                            {reviewBadges[0].name}
                        </span>
                    )}
                </div>
                <div className="HitInfo__details">
                    <Details listing={listing} isMetric={isMetric} />
                </div>
                <Experiment name={HA_SERP_MOBILE_GEO_DISTANCE}>
                    {({On, Off}) => (
                        <React.Fragment>
                            <On>
                                {geoDistance && (
                                    <div className="HitInfo__distance">
                                        <GeoDistance
                                            geoDistance={geoDistance}
                                            brand={brand}
                                        />
                                    </div>
                                )}
                            </On>
                            <Off>
                                {minStayRange &&
                                    minStayRange.minStayLow !== 0 &&
                                    minStayRange.minStayHigh !== 0 &&
                                    isDatelessSearch && (
                                        <div className="HitInfo__minStay">
                                            <FormattedMessage
                                                id="minStayLabel"
                                                description="min stay"
                                                defaultMessage="{MIN_STAY} night min stay"
                                                values={{
                                                    MIN_STAY:
                                                        minStayRange.minStayLow !==
                                                        minStayRange.minStayHigh
                                                            ? `${
                                                                  minStayRange.minStayLow
                                                              } - ${
                                                                  minStayRange.minStayHigh
                                                              }`
                                                            : `${
                                                                  minStayRange.minStayLow
                                                              }`
                                                }}
                                            />
                                        </div>
                                    )}
                            </Off>
                        </React.Fragment>
                    )}
                </Experiment>

                <div className="HitInfo__badgeRatingGroup">
                    {reviewCount > 0 && (
                        <div className="HitInfo__rating">
                            <Rating
                                listing={filterGraphQlAnywhere(
                                    ratingFragments.listing,
                                    listing
                                )}
                            />
                        </div>
                    )}
                    {premierPartnerBadge && (
                        <div
                            className="HitInfo__premierBadge bg-brand small"
                            data-wdio="premier-partner-badge"
                        >
                            {premierPartnerBadge.name}
                        </div>
                    )}
                </div>
                {(!!exclusivityBadge || freeCancellationUntil) && (
                    <div className="HitInfo__item HitInfo__labels">
                        {!!exclusivityBadge && (
                            <HitLabel
                                brand={brand}
                                id={exclusivityBadge.id}
                                label={exclusivityBadge.name}
                            />
                        )}
                        {freeCancellationUntil && (
                            <Experiment
                                name={HA_SERP_FREE_CANCELLATION_V2}
                                variant={Experiment.ON}
                            >
                                <HitFreeCancellation
                                    formatMessage={intl.formatMessage}
                                />
                            </Experiment>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
